import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Edit2, Send, CheckCircle, XCircle, Ban,
    Clock, FileText, User, Building2, Calendar, ArrowLeft
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Button, Badge, toast, ConfirmDialog
} from '../../components/common';
import { purchaseRequestApi } from '../../services/api';
import useAuthStore from '../../stores/authStore';
import './PurchaseRequestDetail.css';

export default function PurchaseRequestDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthStore();
    const [actionType, setActionType] = React.useState('');

    const { data: pr, isLoading } = useQuery({
        queryKey: ['purchaseRequest', id],
        queryFn: () => purchaseRequestApi.get(id),
        select: (res) => res.data?.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ action }) => {
            switch (action) {
                case 'submit': return purchaseRequestApi.submit(id);
                case 'approve': return purchaseRequestApi.approve(id, {});
                case 'reject': return purchaseRequestApi.reject(id, { reason: 'Rejected by approver' });
                case 'cancel': return purchaseRequestApi.cancel(id);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            const messages = {
                submit: 'Purchase Request submitted for approval',
                approve: 'Purchase Request approved successfully',
                reject: 'Purchase Request rejected',
                cancel: 'Purchase Request cancelled'
            };
            toast.success(messages[action]);
            queryClient.invalidateQueries(['purchaseRequest', id]);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const variants = {
            'draft': 'default',
            'pending_approval': 'warning',
            'approved': 'success',
            'rejected': 'danger',
            'cancelled': 'default',
            'completed': 'info'
        };
        return <Badge variant={variants[status] || 'default'}>{status?.replace('_', ' ')}</Badge>;
    };

    if (isLoading) {
        return <div className="loading-state">Loading...</div>;
    }

    if (!pr) {
        return (
            <div className="error-state">
                <p>Purchase Request not found</p>
                <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate('/purchase-requests')}>
                    Back to List
                </Button>
            </div>
        );
    }

    const canEdit = pr.status === 'draft' && pr.requesterId === user?.id;
    const canSubmit = pr.status === 'draft' && pr.requesterId === user?.id;

    // Check if current user is a pending approver at the current level
    const pendingApproval = pr.approvals?.find(
        a => a.approverId === user?.id &&
            a.action === 'pending' &&
            a.approvalLevel === pr.currentApprovalLevel
    );
    const canApprove = pr.status === 'pending_approval' && !!pendingApproval;

    // Debug logging
    console.log('DEBUG Approval Check:', {
        userId: user?.id,
        prStatus: pr.status,
        currentLevel: pr.currentApprovalLevel,
        approvals: pr.approvals?.map(a => ({ approverId: a.approverId, action: a.action, level: a.approvalLevel })),
        pendingApproval,
        canApprove
    });

    const canCancel = ['draft', 'pending_approval'].includes(pr.status) && pr.requesterId === user?.id;

    // Use lines from API response
    const lines = pr.lines || pr.items || [];

    return (
        <div className="pr-detail-page">
            <PageHeader
                title={`Purchase Request: ${pr.prNumber}`}
                breadcrumbs={[
                    { label: 'Purchase Requests', path: '/purchase-requests' },
                    { label: pr.prNumber }
                ]}
                actions={
                    <div className="pr-actions">
                        {canEdit && (
                            <Button variant="secondary" icon={Edit2} onClick={() => navigate(`/purchase-requests/${id}/edit`)}>
                                Edit
                            </Button>
                        )}
                        {canSubmit && (
                            <Button icon={Send} onClick={() => setActionType('submit')}>
                                Submit
                            </Button>
                        )}
                        {canApprove && (
                            <>
                                <Button variant="secondary" icon={XCircle} onClick={() => setActionType('reject')}>
                                    Reject
                                </Button>
                                <Button icon={CheckCircle} onClick={() => setActionType('approve')}>
                                    Approve
                                </Button>
                            </>
                        )}
                        {canCancel && (
                            <Button variant="danger" icon={Ban} onClick={() => setActionType('cancel')}>
                                Cancel
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="pr-detail-content">
                <div className="pr-detail-main">
                    <Card>
                        <CardBody>
                            <div className="pr-header">
                                <div>
                                    <h2>{pr.title}</h2>
                                    <p className="pr-number">{pr.prNumber}</p>
                                </div>
                                {getStatusBadge(pr.status)}
                            </div>

                            {pr.description && (
                                <div className="pr-description">
                                    <p>{pr.description}</p>
                                </div>
                            )}

                            <div className="pr-meta-grid">
                                <div className="meta-item">
                                    <User size={16} />
                                    <span className="meta-label">Requester</span>
                                    <span className="meta-value">{pr.requester?.fullName || 'Unknown'}</span>
                                </div>
                                <div className="meta-item">
                                    <Building2 size={16} />
                                    <span className="meta-label">Department</span>
                                    <span className="meta-value">{pr.department?.name || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <Calendar size={16} />
                                    <span className="meta-label">Required Date</span>
                                    <span className="meta-value">{formatDate(pr.requiredDate)}</span>
                                </div>
                                <div className="meta-item">
                                    <Clock size={16} />
                                    <span className="meta-label">Created</span>
                                    <span className="meta-value">{formatDate(pr.createdAt)}</span>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody>
                            <h3>Line Items</h3>
                            <div className="pr-items-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Item</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>Unit Price</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lines.map((item, index) => (
                                            <tr key={item.id || index}>
                                                <td>{item.lineNumber || index + 1}</td>
                                                <td>
                                                    <div className="item-info">
                                                        <span className="item-code">{item.itemCode || item.catalogItem?.itemCode || '-'}</span>
                                                        <span className="item-name">{item.itemName || item.catalogItem?.itemName || item.catalogItem?.name || '-'}</span>
                                                    </div>
                                                </td>
                                                <td>{item.specifications || item.description || '-'}</td>
                                                <td>{item.quantity} {item.uomCode || item.catalogItem?.primaryUom?.code || 'PCS'}</td>
                                                <td>{formatCurrency(item.estimatedPrice || item.unitPrice)}</td>
                                                <td className="total">{formatCurrency(item.totalPrice || (item.quantity * (item.estimatedPrice || item.unitPrice)))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td colSpan="5" className="grand-total-label">Grand Total</td>
                                            <td className="grand-total-value">{formatCurrency(pr.totalAmount)}</td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </CardBody>
                    </Card>
                </div>

                <div className="pr-detail-sidebar">
                    <Card>
                        <CardBody>
                            <h4>Priority</h4>
                            <Badge variant={pr.priority === 'high' ? 'danger' : pr.priority === 'medium' ? 'warning' : 'default'}>
                                {pr.priority}
                            </Badge>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody>
                            <h4>Budget Allocation</h4>
                            {pr.budgetPlan ? (
                                <div className="budget-info-detail">
                                    <div style={{ marginBottom: '0.75rem' }}>
                                        <span style={{
                                            fontSize: '1rem',
                                            fontWeight: 700,
                                            color: '#3b82f6',
                                            display: 'block'
                                        }}>
                                            {pr.budgetPlan.budgetCode}
                                        </span>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            color: '#64748b'
                                        }}>
                                            {pr.budgetPlan.department?.name || 'N/A'}
                                        </span>
                                    </div>

                                    <div style={{
                                        display: 'grid',
                                        gap: '0.5rem',
                                        fontSize: '0.8125rem'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#64748b' }}>Total Budget</span>
                                            <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                                {formatCurrency(pr.budgetPlan.totalAmount)}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#64748b' }}>Committed</span>
                                            <span style={{ fontWeight: 600, color: '#f59e0b' }}>
                                                {formatCurrency(pr.budgetPlan.committedAmount || 0)}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#64748b' }}>Spent</span>
                                            <span style={{ fontWeight: 600, color: '#ef4444' }}>
                                                {formatCurrency(pr.budgetPlan.spentAmount || 0)}
                                            </span>
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            paddingTop: '0.5rem',
                                            borderTop: '1px solid #e2e8f0'
                                        }}>
                                            <span style={{ color: '#16a34a', fontWeight: 600 }}>Available</span>
                                            <span style={{ fontWeight: 700, color: '#16a34a' }}>
                                                {formatCurrency(
                                                    parseFloat(pr.budgetPlan.totalAmount || 0) -
                                                    parseFloat(pr.budgetPlan.committedAmount || 0) -
                                                    parseFloat(pr.budgetPlan.spentAmount || 0)
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Utilization Bar */}
                                    <div style={{ marginTop: '0.75rem' }}>
                                        <div style={{
                                            height: '6px',
                                            background: '#e2e8f0',
                                            borderRadius: '3px',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                height: '100%',
                                                width: `${Math.min(
                                                    ((parseFloat(pr.budgetPlan.committedAmount || 0) +
                                                        parseFloat(pr.budgetPlan.spentAmount || 0)) /
                                                        parseFloat(pr.budgetPlan.totalAmount || 1)) * 100,
                                                    100
                                                )}%`,
                                                background: 'linear-gradient(90deg, #22c55e 0%, #f59e0b 100%)',
                                                borderRadius: '3px',
                                                transition: 'width 0.3s ease'
                                            }} />
                                        </div>
                                        <p style={{
                                            margin: '0.25rem 0 0',
                                            fontSize: '0.6875rem',
                                            color: '#94a3b8',
                                            textAlign: 'right'
                                        }}>
                                            {Math.round(
                                                ((parseFloat(pr.budgetPlan.committedAmount || 0) +
                                                    parseFloat(pr.budgetPlan.spentAmount || 0)) /
                                                    parseFloat(pr.budgetPlan.totalAmount || 1)) * 100
                                            )}% utilized
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <p className="no-budget" style={{
                                    color: '#94a3b8',
                                    fontStyle: 'italic',
                                    fontSize: '0.875rem'
                                }}>
                                    No budget allocated
                                </p>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>

            <ConfirmDialog
                isOpen={!!actionType}
                onClose={() => setActionType('')}
                onConfirm={() => actionMutation.mutate({ action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Purchase Request`}
                message={`Are you sure you want to ${actionType} this purchase request?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                variant={['reject', 'cancel'].includes(actionType) ? 'danger' : 'primary'}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
